import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import {
  deriveMonthSpan, monthRange, periodLabel, activePresetKey, stepRange,
  PRESETS_ANALYTIC,
} from '../utils/period';
import { useIsMobile } from '../utils/useIsMobile';
import MonthGrid from './MonthGrid';
import DayRangeView from './DayRangeView';
import Icon from './Icon';
import { useSheetDrag } from '../utils/useSheetDrag';

// The single date control for every filter bar, replacing the old
// MonthPicker + DateRangePicker pair.
//
// MONTH-FIRST BY DESIGN. This product is an accounting-period tool, not an
// analytics dashboard: with שוטף payment terms an invoice month and its payment
// month differ by one to four months (server/lib/dueDate.js), so on any given
// day a user is reconciling four or five *different* named months at once.
// Reaching an arbitrary month therefore has to be fast, and the popover opens
// on the month grid. The day calendar is still there, as a labelled escape
// hatch behind the חודשים / טווח ימים switch.
//
// A segmented switch rather than a tappable calendar caption: months are the
// primary unit here, and a caption drill-down is undiscoverable to anyone who
// has never thought to tap a month name.

const YEAR_SPAN = 10;   // how far the grid's year nav may travel from today

export default function PeriodPicker({
  dateFrom: from,
  dateTo: to,
  onChange,
  presets = PRESETS_ANALYTIC,
  // 'pill' — the desktop control, sitting in the filter row.
  // 'bar'  — mobile: a full-width period navigator on its own sticky line
  //          above the filters. The date is page SCOPE, not a filter, and at
  //          375px a shared row can't hold a label like
  //          "נובמבר 2025 – פברואר 2026" without ellipsising the one thing on
  //          screen the user most needs to read.
  variant = 'pill',
  disabled = false,
  disabledReason = '',
  // The picked range runs to today or past it, so documents for it are still
  // arriving. Tints the calendar icon amber — the same signal, and the same
  // hue, that "כל הזמן" already uses for "the figures on this page are not
  // what you would assume". The RULE is not computed here on purpose: the page
  // that owns the figures owns the definition, and passes the answer down.
  openPeriod = false,
}) {
  const [open, setOpen] = useState(false);
  // anchor: {top, right} fixed-coords for desktop. Null until first measure.
  const [anchor, setAnchor] = useState(null);
  const [view, setView] = useState('months');
  const [gridYear, setGridYear] = useState(() => new Date().getFullYear());
  // isMobile flips positioning between dropdown (desktop) and bottom-sheet
  // (mobile). 720px matches the CSS breakpoint; the shared hook re-renders
  // on rotation / resize so an open popover updates its layout.
  const isMobile = useIsMobile();
  const wrapRef = useRef(null);
  const popRef = useRef(null);
  // On a phone the picker is a bottom sheet: swipe down closes it, as its handle promises.
  const closePicker = useCallback(() => setOpen(false), []);
  const popBackdropRef = useRef(null);
  useSheetDrag(popRef, closePicker, { enabled: open && isMobile, backdropRef: popBackdropRef });
  // Phone sheet: lock the page behind it, like FilterSheet, so a swipe on the
  // sheet's non-scrolling parts cannot scroll the page.
  useEffect(() => {
    if (!open || !isMobile) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [open, isMobile]);

  const span = useMemo(() => deriveMonthSpan(from, to), [from, to]);
  const activeKey = useMemo(() => activePresetKey(from, to, presets), [from, to, presets]);
  const label = useMemo(() => periodLabel(from, to), [from, to]);

  // Which view to show is DERIVED when the popover opens, never stored across
  // opens: a month-aligned range (or no range at all) opens on the grid, an
  // arbitrary one opens on the calendar, so the control can never misrepresent
  // what is actually applied — including a range restored from sessionStorage
  // by sharedDateRange.js.
  //
  // Keyed on `open` alone, on purpose. Re-deriving on every from/to change
  // would yank the user back to the grid the moment they clicked a day that
  // happened to land on the 1st.
  useEffect(() => {
    if (!open) return;
    const s = deriveMonthSpan(from, to);
    setView((!from && !to) || s ? 'months' : 'days');
    const anchorYm = s ? s.startYm : String(from || to || '').slice(0, 7);
    setGridYear(anchorYm ? Number(anchorYm.slice(0, 4)) : new Date().getFullYear());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Measure the trigger when opening so we can portal the popover and still
  // anchor it visually. Re-measure on scroll/resize while open.
  useEffect(() => {
    if (!open || isMobile) return;
    const measure = () => {
      if (!wrapRef.current) return;
      const r = wrapRef.current.getBoundingClientRect();
      // Popover width — must match the CSS .docs-filter-datepop { width: 460px }.
      // The popover is anchored by its right edge and extends leftward; clamp
      // so its left edge never goes past 8px from the viewport edge. Without
      // this, in RTL the trigger sits at the start (= visual left), so the
      // raw `right` anchor would push the popover off the left edge.
      const POP_W = 460;
      const desired = window.innerWidth - r.right;
      const maxRight = window.innerWidth - POP_W - 8; // popover left edge ≥ 8
      setAnchor({
        top: r.bottom + 6,
        right: Math.max(8, Math.min(desired, maxRight)),
      });
    };
    measure();
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, true);
    return () => {
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure, true);
    };
  }, [open, isMobile]);

  useEffect(() => {
    if (!open) return;
    // Close on click outside BOTH the trigger and the portaled popover.
    const onDown = (e) => {
      const inTrigger = wrapRef.current && wrapRef.current.contains(e.target);
      const inPop = popRef.current && popRef.current.contains(e.target);
      if (!inTrigger && !inPop) setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // Single choke point for every path that sets a range — grid, presets and
  // both day-view inputs — so the from>to guard can't be bypassed. The manual
  // <input type="date"> fields accept an inverted pair happily, and the query
  // it produces returns zero rows with nothing to explain why; swapping is
  // unambiguous about what the user meant.
  const apply = useCallback((nextFrom, nextTo) => {
    if (nextFrom && nextTo && nextFrom > nextTo) onChange(nextTo, nextFrom);
    else onChange(nextFrom || '', nextTo || '');
  }, [onChange]);

  const pickMonth = useCallback((ym) => {
    const r = monthRange(Number(ym.slice(0, 4)), Number(ym.slice(5)) - 1);
    apply(r.from, r.to);
    setOpen(false);      // a month pick fully determines the range — nothing left to confirm
  }, [apply]);

  const pickPreset = useCallback((p) => {
    const r = p.range();
    apply(r.from, r.to);
    setOpen(false);      // likewise complete
  }, [apply]);

  const isActive = Boolean(from || to);
  const thisYear = new Date().getFullYear();
  const todayYm = `${thisYear}-${String(new Date().getMonth() + 1).padStart(2, '0')}`;
  const isBar = variant === 'bar';

  // One-tap month stepping. Null for arbitrary day ranges, one-sided ranges and
  // כל הזמן — there is no defensible "next month" for those, so the arrows go
  // disabled rather than guessing. Disabled and NOT hidden: hiding them would
  // resize the label mid-use, which also changes the measured sticky height.
  const stepTo = useCallback((dir) => {
    const next = stepRange(from, to, dir);
    if (!next) return;
    // Stay inside the month grid's own year window, so the bar can't walk into
    // a period the picker would then refuse to display.
    const y = Number(next.from.slice(0, 4));
    if (y < thisYear - YEAR_SPAN || y > thisYear + YEAR_SPAN) return;
    apply(next.from, next.to);
  }, [from, to, apply, thisYear]);
  const canStep = Boolean(deriveMonthSpan(from, to)) && !disabled;

  const trigger = (
    <button
      type="button"
      className={
        isBar
          ? `period-bar-label${isActive ? '' : ' is-unbounded'}`
          : `docs-filter-control is-date${isActive ? ' is-active' : ''}`
      }
      onClick={() => !disabled && setOpen((o) => !o)}
      disabled={disabled}
      title={disabled ? disabledReason : (openPeriod ? `${label} — התקופה טרם נסגרה` : label)}
      aria-haspopup="dialog"
      aria-expanded={open}
      aria-label={`טווח תאריכים: ${label}${openPeriod ? ', התקופה טרם נסגרה' : ''}`}
    >
      {isBar
        ? <Icon name="calendar" size={15} className={`period-bar-cal${openPeriod ? ' is-open-period' : ''}`} />
        : (
          <>
            {/* The pill has no calendar icon in its normal state, so the amber
                one appears only when there is something to say. */}
            {openPeriod && <Icon name="calendar" size={13} className="period-pill-cal is-open-period" />}
            <span className="docs-filter-microlabel">טווח תאריכים</span>
          </>
        )}
      <span className="docs-filter-pillvalue" aria-live="polite">{label}</span>
      <Icon name="chevron" size={12} className="docs-filter-caret" />
    </button>
  );

  return (
    <div className={isBar ? 'period-bar-inner' : 'docs-filter-datewrap'} ref={wrapRef}>
      {isBar ? (
        <>
          {/* DOM order [prev, label, next]; under RTL that puts "previous" on
              the right, matching MonthGrid's year nav. */}
          <button
            type="button"
            className="period-bar-step"
            onClick={() => stepTo(-1)}
            disabled={!canStep}
            aria-label="תקופה קודמת"
          >
            <Icon name="chevron-inline" size={18} className="period-grid-chev-prev" />
          </button>
          {trigger}
          <button
            type="button"
            className="period-bar-step"
            onClick={() => stepTo(1)}
            disabled={!canStep}
            aria-label="תקופה הבאה"
          >
            <Icon name="chevron-inline" size={18} />
          </button>
        </>
      ) : trigger}

      {open && createPortal(
        <>
          {/* Backdrop — visible on mobile only (CSS hides on desktop).
              Clicking it closes the sheet. */}
          <div
            ref={popBackdropRef}
            className="docs-filter-datepop-backdrop"
            onClick={() => setOpen(false)}
            aria-hidden
          />
          <div
            ref={popRef}
            className="docs-filter-datepop"
            role="dialog"
            aria-label="טווח תאריכים"
            // Use the *physical* `right` property (not the logical
            // `inset-inline-end`) so the popover lands beside the trigger
            // regardless of document direction. In RTL the body has dir="rtl"
            // and `inset-inline-end` flips to mean `left`, which was sending
            // the popover to the opposite side of the viewport.
            style={isMobile ? undefined : (anchor ? { top: anchor.top, right: anchor.right } : { visibility: 'hidden' })}
          >
            <div className="docs-filter-datepop-handle" aria-hidden />
            <div className="docs-filter-datepop-header">
              <span className="docs-filter-datepop-title">טווח תאריכים</span>
              <button
                type="button"
                className="docs-filter-datepop-close"
                onClick={() => setOpen(false)}
                aria-label="סגור"
              >×</button>
            </div>

            {/* Month / day switch. Focus deliberately stays on the pressed
                segment when the body swaps — the segment lives in the header
                and is never unmounted, so focus can't land on a detached node
                and there is nothing to restore. */}
            <div className="docs-filter-viewswitch" role="group" aria-label="אופן בחירה">
              <button
                type="button"
                className={`docs-filter-viewseg${view === 'months' ? ' is-active' : ''}`}
                aria-pressed={view === 'months'}
                onClick={() => setView('months')}
              >חודשים</button>
              <button
                type="button"
                className={`docs-filter-viewseg${view === 'days' ? ' is-active' : ''}`}
                aria-pressed={view === 'days'}
                onClick={() => setView('days')}
              >טווח ימים</button>
            </div>

            <div className="docs-filter-datepop-grid">
              {/* Presets (right side under RTL — where Hebrew eyes land first). */}
              <aside className="docs-filter-presets">
                {presets.map((p) => (
                  <button
                    key={p.key}
                    type="button"
                    className={`docs-filter-preset${activeKey === p.key ? ' is-active' : ''}`}
                    onClick={() => pickPreset(p)}
                  >
                    {p.label}
                  </button>
                ))}
              </aside>

              <div className="docs-filter-datepop-main">
                {view === 'months' ? (
                  <MonthGrid
                    year={gridYear}
                    onYearChange={setGridYear}
                    span={span}
                    todayYm={todayYm}
                    onPick={pickMonth}
                    minYear={thisYear - YEAR_SPAN}
                    maxYear={thisYear + YEAR_SPAN}
                  />
                ) : (
                  <DayRangeView dateFrom={from} dateTo={to} onChange={apply} />
                )}
              </div>
            </div>

            <div className="docs-filter-datepop-footer">
              <button
                type="button"
                className="docs-filter-clearall"
                onClick={() => apply('', '')}
              >נקה טווח</button>
              <button
                type="button"
                className="docs-filter-done"
                onClick={() => setOpen(false)}
              >סיום</button>
            </div>
          </div>
        </>,
        document.body
      )}
    </div>
  );
}
